import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { MdCallSplit, MdChevronRight, MdExpandMore } from "react-icons/md";
import { ProjectTask, TaskType } from "../../../../interfaces/ProjectTask";
import { epicColor } from "../../../../utils/epicColor";

interface Line {
  date: string; // yyyy-mm-dd
  plannedHours: number;
}

interface Props {
  tasks: ProjectTask[];
  onEdit: (task: ProjectTask) => void;
  showProject?: boolean;
  /** Quando true, permite mover/redimensionar/quebrar o cronograma com o mouse. */
  editable?: boolean;
  /** Cronograma por tarefa (taskId → linhas dia-a-dia). */
  schedules?: Record<number, Line[]>;
  /** Capacity semanal por colaborador (id → horas/semana) para o overlay "cabe ou não". */
  employeesCapacity?: Record<number, number>;
  /** Chamado ao soltar/redimensionar/quebrar — persiste o novo cronograma da tarefa. */
  onScheduleChange?: (taskId: number, lines: Line[]) => void;
  /**
   * Como as linhas são agrupadas. `epic` é a árvore de sempre (épico → tarefa → subtarefa);
   * `project` e `client` achatam a hierarquia e agrupam pelo cabeçalho correspondente.
   */
  groupBy?: GanttGroupBy;
  /** `projectId` → cliente do projeto. Necessário só para `groupBy="client"`. */
  projectClients?: Record<number, { clientId?: number | null; clientName?: string | null }>;
  /**
   * Portal do cliente: não mostra responsável, horas nem aviso de capacity. Esses dados não chegam
   * ao cliente, e exibir "Sem responsável" ou "0h" afirmaria algo falso (ausência não é zero).
   */
  modoCliente?: boolean;
}

export type GanttGroupBy = "epic" | "project" | "client";

export const GANTT_GROUP_LABELS: Record<GanttGroupBy, string> = {
  epic: "Por épico",
  project: "Por projeto",
  client: "Por cliente",
};

const DAY_MS = 24 * 60 * 60 * 1000;
const NAME_W = 224;
const MIN_PPD = 2;
const MAX_PPD = 48;
const SEGMENT_GAP = 3; // dias de intervalo que ainda contam como o MESMO bloco (ponte de fim de semana)

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
const pad = (n: number) => String(n).padStart(2, "0");
const dateKey = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseKey = (k: string) => {
  const [y, m, d] = k.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const addDays = (d: Date, n: number) => new Date(d.getTime() + n * DAY_MS);

const isoMonday = (d: Date) => {
  const date = startOfDay(d);
  const diff = (date.getDay() + 6) % 7;
  return new Date(date.getTime() - diff * DAY_MS);
};

const barColor = (task: ProjectTask) => {
  switch (task.scheduleState) {
    case "overdue":
      return "bg-red-500";
    case "due_soon":
      return "bg-amber-500";
    case "done":
      return "bg-green-500";
    case "cancelled":
      return "bg-gray-400";
    default:
      return "bg-brand-500";
  }
};

// Textura (listras diagonais) da barra de tarefa CONCLUÍDA — distingue do verde sólido
// mesmo em impressão/daltonismo.
const DONE_STRIPES =
  "repeating-linear-gradient(45deg, rgba(255,255,255,.35) 0 5px, transparent 5px 10px)";

type Granularity = "day" | "week" | "month";
const granularityOf = (ppd: number): Granularity => (ppd >= 22 ? "day" : ppd >= 7 ? "week" : "month");
const GRAN_LABEL: Record<Granularity, string> = { day: "Dia", week: "Semana", month: "Mês" };

interface Column {
  leftPx: number;
  widthPx: number;
  label: string;
  weekend?: boolean;
}

interface Segment {
  start: Date;
  end: Date;
  dates: string[]; // yyyy-mm-dd dos dias do segmento
  total: number;
}

// Agrupa as linhas em segmentos (blocos). Intervalo > SEGMENT_GAP dias inicia um novo segmento ("2 momentos").
const buildSegments = (lines: Line[]): Segment[] => {
  const sorted = lines.filter((l) => l.plannedHours > 0).slice().sort((a, b) => (a.date < b.date ? -1 : 1));
  const segs: Segment[] = [];
  let cur: Line[] = [];
  let prev: Date | null = null;
  const flush = () => {
    if (!cur.length) return;
    const dates = cur.map((l) => l.date);
    segs.push({
      start: parseKey(dates[0]),
      end: parseKey(dates[dates.length - 1]),
      dates,
      total: cur.reduce((a, l) => a + l.plannedHours, 0),
    });
    cur = [];
  };
  for (const l of sorted) {
    const d = parseKey(l.date);
    if (prev && (d.getTime() - prev.getTime()) / DAY_MS > SEGMENT_GAP) flush();
    cur.push(l);
    prev = d;
  }
  flush();
  return segs;
};

/**
 * Uma linha do gráfico. O cabeçalho de grupo (projeto/cliente) não é uma tarefa — não tem
 * responsável, capacity nem barra arrastável — e por isso é um caso próprio da união, e não uma
 * `ProjectTask` sintética. Tarefa falsa precisaria de id, tipo e estado inventados, e cada um deles
 * viraria um `if` espalhado pelo render.
 */
type GanttRow =
  | { kind: "group"; key: string; label: string; tasks: ProjectTask[] }
  | { kind: "task"; task: ProjectTask; depth: number; kids: boolean };

interface DragState {
  taskId: number;
  segIndex: number;
  mode: "move" | "resize";
  startX: number;
  deltaDays: number;
}

const TaskGantt: React.FC<Props> = ({
  tasks,
  onEdit,
  showProject,
  editable = false,
  schedules,
  employeesCapacity,
  onScheduleChange,
  groupBy = "epic",
  projectClients,
  modoCliente = false,
}) => {
  const [pxPerDay, setPxPerDay] = useState(16);
  const pxRef = useRef(pxPerDay);
  pxRef.current = pxPerDay;
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const anchorRef = useRef<{ day: number; vx: number } | null>(null);

  // Cópia de trabalho do cronograma (atualiza na hora ao arrastar; sincroniza com as props).
  const [draft, setDraft] = useState<Record<number, Line[]>>(schedules ?? {});
  useEffect(() => setDraft(schedules ?? {}), [schedules]);

  const [drag, setDrag] = useState<DragState | null>(null);
  const didDragRef = useRef(false); // distingue "arrastou" de "clicou" (evita abrir o modal após arrastar)
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  // Colapso dos cabeçalhos de projeto/cliente. Estado à parte do `collapsed` das tarefas porque a
  // chave é outra (string) e os dois convivem: trocar de agrupamento não pode desfazer o que a
  // pessoa recolheu na árvore de épicos.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const toggleGroup = (key: string) =>
    setCollapsedGroups((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  const toggle = (id: number) =>
    setCollapsed((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el && anchorRef.current) {
      const { day, vx } = anchorRef.current;
      el.scrollLeft = NAME_W + day * pxPerDay - vx;
      anchorRef.current = null;
    }
  }, [pxPerDay]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      // ⚠ Só o gesto de PINÇA dá zoom. Navegadores entregam a pinça de trackpad como um `wheel`
      // com `ctrlKey` — é a convenção que Chrome, Edge, Firefox e Safari usam para distinguir
      // "aproximar" de "rolar". Antes, todo scroll vertical era capturado: quem tentava descer a
      // página em cima do cronograma dava zoom sem querer, e não havia como descer a página sem
      // tirar o mouse do gráfico.
      //
      // Sem `ctrlKey` a função retorna SEM `preventDefault`, então o evento segue seu caminho e a
      // página rola normalmente. `Ctrl+scroll` de mouse cai aqui de propósito: é a mesma convenção.
      if (!e.ctrlKey || e.shiftKey) return;

      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const vx = e.clientX - rect.left;
      const day = Math.max(0, (vx + el.scrollLeft - NAME_W) / pxRef.current);
      // A pinça manda deltas bem maiores que a roda; o passo é proporcional para o gesto não
      // atravessar a faixa inteira de zoom num movimento só.
      const passo = Math.min(1.25, 1 + Math.abs(e.deltaY) / 100);
      const factor = e.deltaY < 0 ? passo : 1 / passo;
      const next = clamp(pxRef.current * factor, MIN_PPD, MAX_PPD);
      if (next === pxRef.current) return;
      anchorRef.current = { day, vx };
      setPxPerDay(next);
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  const zoomBy = (factor: number) => setPxPerDay((prev) => clamp(prev * factor, MIN_PPD, MAX_PPD));

  /** Quando a barra da tarefa começa — do cronograma quando existe, senão das datas. */
  const spanOfTask = (task: ProjectTask): Date | null => {
    const lines = draft[task.id];
    if (lines?.length) return parseKey(lines.map((l) => l.date).sort()[0]);

    const d = task.startDate || task.dueDate;
    return d ? startOfDay(new Date(d)) : null;
  };

  // Linhas efetivas de uma tarefa (draft → senão deriva de start/dueDate como bloco simples).
  const linesOf = (task: ProjectTask): Line[] => {
    const d = draft[task.id];
    if (d && d.length) return d;
    return [];
  };

  // Árvore (épico → tarefa → subtarefa) a partir de parentTaskId/type.
  const tree = useMemo(() => {
    const childrenOf = new Map<number, ProjectTask[]>();
    const byId = new Map<number, ProjectTask>();
    tasks.forEach((t) => byId.set(t.id, t));
    tasks.forEach((t) => {
      if (t.parentTaskId != null) {
        const arr = childrenOf.get(t.parentTaskId) ?? [];
        arr.push(t);
        childrenOf.set(t.parentTaskId, arr);
      }
    });
    const order = (a: ProjectTask, b: ProjectTask) => a.orderIndex - b.orderIndex || a.id - b.id;
    childrenOf.forEach((arr) => arr.sort(order));
    const topLevel = tasks.filter((t) => t.parentTaskId == null).sort(order);
    return { childrenOf, byId, topLevel };
  }, [tasks]);

  // Planejado por colaborador/dia — SÓ das folhas (pais/épicos não consomem capacity).
  const plannedByEmpDay = useMemo(() => {
    const m = new Map<string, number>();
    tasks.forEach((t) => {
      if (t.assigneeEmployeeId == null) return;
      if ((tree.childrenOf.get(t.id)?.length ?? 0) > 0) return; // pula pais
      (draft[t.id] ?? []).forEach((l) => {
        const k = `${t.assigneeEmployeeId}|${l.date}`;
        m.set(k, (m.get(k) ?? 0) + l.plannedHours);
      });
    });
    return m;
  }, [tasks, draft, tree]);

  const dailyCapOf = (empId?: number | null) => {
    const weekly = (empId != null ? employeesCapacity?.[empId] : undefined) ?? 40;
    return weekly > 0 ? weekly / 5 : 8;
  };

  // Visão: tarefas com cronograma/datas + intervalo total (memoizado p/ o auto-fit).
  const view = useMemo(() => {
    const has = (t: ProjectTask) => (draft[t.id]?.length ?? 0) > 0 || !!t.startDate || !!t.dueDate;
    const dated = tasks.filter(has);
    const undated = tasks.filter((t) => !has(t));
    let min = Infinity;
    let max = -Infinity;
    dated.forEach((t) => {
      const lines = draft[t.id] ?? [];
      if (lines.length) {
        lines.forEach((l) => {
          const ms = parseKey(l.date).getTime();
          min = Math.min(min, ms);
          max = Math.max(max, ms);
        });
      } else {
        const s = startOfDay(new Date(t.startDate || t.dueDate!)).getTime();
        const e = startOfDay(new Date(t.dueDate || t.startDate!)).getTime();
        min = Math.min(min, s);
        max = Math.max(max, e);
      }
    });
    if (!isFinite(min)) return null;
    const rangeStart = isoMonday(new Date(min));
    const rangeEnd = addDays(startOfDay(new Date(max)), 7); // folga p/ arrastar à direita
    const totalDays = Math.max(1, Math.round((rangeEnd.getTime() - rangeStart.getTime()) / DAY_MS) + 1);
    return { dated, undated, rangeStart, rangeEnd, totalDays };
  }, [tasks, draft]);

  // Auto-fit: px/dia dimensionado p/ o cronograma PREENCHER a largura disponível (re-fit no resize/dados).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !view) return;
    const fit = () => {
      const w = el.clientWidth;
      if (w > 0) setPxPerDay(clamp((w - NAME_W) / view.totalDays, MIN_PPD, MAX_PPD));
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view?.totalDays]);

  // Arraste (mover / redimensionar) com listeners de janela. Hook ANTES de qualquer early return.
  useEffect(() => {
    if (!drag) return;
    const onMove = (e: MouseEvent) => {
      setDrag((d) => {
        if (!d) return d;
        const deltaDays = Math.round((e.clientX - d.startX) / pxRef.current);
        if (deltaDays !== 0) didDragRef.current = true;
        return { ...d, deltaDays };
      });
    };
    const onUp = () => {
      setDrag((d) => {
        if (d && d.deltaDays !== 0) {
          const task = tasks.find((t) => t.id === d.taskId);
          if (task) {
            const seg = buildSegments(draft[task.id] ?? [])[d.segIndex];
            if (seg) {
              if (d.mode === "move") moveSegment(task, seg, d.deltaDays);
              else resizeSegment(task, seg, d.deltaDays);
            }
          }
        }
        return null;
      });
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag?.taskId, drag?.segIndex, drag?.mode]);

  if (!view) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-200 py-12 text-center text-sm text-gray-700 dark:border-white/10 dark:text-gray-300">
        Nenhuma tarefa com cronograma. Mova uma tarefa do Backlog para "A Fazer" no quadro para habilitar o cronograma.
      </div>
    );
  }

  const { dated: datedTasks, undated, rangeStart, rangeEnd, totalDays } = view;
  const chartWidth = totalDays * pxPerDay;
  const offsetOf = (d: Date) => (startOfDay(d).getTime() - rangeStart.getTime()) / DAY_MS;
  const doFit = () => {
    const el = scrollRef.current;
    if (el && el.clientWidth > 0) setPxPerDay(clamp((el.clientWidth - NAME_W) / totalDays, MIN_PPD, MAX_PPD));
  };

  const granularity = granularityOf(pxPerDay);
  const fmtDM = (d: Date) => d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  const fmtMonth = (d: Date) => d.toLocaleDateString("pt-BR", { month: "short", year: "2-digit" });

  const columns: Column[] = [];
  if (granularity === "day") {
    for (let i = 0; i < totalDays; i++) {
      const d = new Date(rangeStart.getTime() + i * DAY_MS);
      const weekend = d.getDay() === 0 || d.getDay() === 6;
      columns.push({
        leftPx: i * pxPerDay,
        widthPx: pxPerDay,
        label: d.getDate() === 1 || i === 0 ? fmtDM(d) : String(d.getDate()),
        weekend,
      });
    }
  } else if (granularity === "week") {
    for (let w = new Date(rangeStart); w.getTime() <= rangeEnd.getTime(); w = new Date(w.getTime() + 7 * DAY_MS)) {
      const offset = Math.round((w.getTime() - rangeStart.getTime()) / DAY_MS);
      columns.push({ leftPx: offset * pxPerDay, widthPx: 7 * pxPerDay, label: fmtDM(w) });
    }
  } else {
    let cursor = new Date(rangeStart.getFullYear(), rangeStart.getMonth(), 1);
    while (cursor.getTime() <= rangeEnd.getTime()) {
      const monthStart = cursor.getTime() < rangeStart.getTime() ? rangeStart : cursor;
      const nextMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1);
      const monthEnd = nextMonth.getTime() - DAY_MS > rangeEnd.getTime() ? rangeEnd : new Date(nextMonth.getTime() - DAY_MS);
      const offset = Math.round((monthStart.getTime() - rangeStart.getTime()) / DAY_MS);
      const days = Math.round((monthEnd.getTime() - monthStart.getTime()) / DAY_MS) + 1;
      columns.push({ leftPx: offset * pxPerDay, widthPx: days * pxPerDay, label: fmtMonth(cursor) });
      cursor = nextMonth;
    }
  }

  const todayOffset = offsetOf(new Date());
  const showToday = todayOffset >= 0 && todayOffset <= totalDays;

  // ── Edição: commit das novas linhas ────────────────────────────────────────
  const commit = (taskId: number, lines: Line[]) => {
    const clean = lines.filter((l) => l.plannedHours > 0).sort((a, b) => (a.date < b.date ? -1 : 1));
    setDraft((prev) => ({ ...prev, [taskId]: clean }));
    onScheduleChange?.(taskId, clean);
  };

  // Substitui as linhas de UM segmento (identificado pelas datas antigas) por novas linhas.
  const replaceSegment = (task: ProjectTask, oldDates: string[], newLines: Line[]) => {
    const old = new Set(oldDates);
    const kept = linesOf(task).filter((l) => !old.has(l.date));
    const merged = new Map<string, number>();
    [...kept, ...newLines].forEach((l) => merged.set(l.date, (merged.get(l.date) ?? 0) + l.plannedHours));
    commit(task.id, Array.from(merged, ([date, plannedHours]) => ({ date, plannedHours })));
  };

  const moveSegment = (task: ProjectTask, seg: Segment, deltaDays: number) => {
    if (deltaDays === 0) return;
    const newLines = seg.dates.map((dk) => ({
      date: dateKey(addDays(parseKey(dk), deltaDays)),
      plannedHours: (draft[task.id] ?? []).find((l) => l.date === dk)?.plannedHours ?? 0,
    }));
    replaceSegment(task, seg.dates, newLines);
  };

  const resizeSegment = (task: ProjectTask, seg: Segment, deltaDays: number) => {
    const spanDays = Math.round((seg.end.getTime() - seg.start.getTime()) / DAY_MS) + 1;
    const newDays = clamp(spanDays + deltaDays, 1, 60);
    if (newDays === spanDays) return;
    const per = Math.round((seg.total / newDays) * 100) / 100;
    const newLines: Line[] = [];
    for (let i = 0; i < newDays; i++) newLines.push({ date: dateKey(addDays(seg.start, i)), plannedHours: per });
    replaceSegment(task, seg.dates, newLines);
  };

  const splitSegment = (task: ProjectTask, seg: Segment) => {
    if (seg.dates.length < 1) return;
    const mid = Math.max(1, Math.ceil(seg.dates.length / 2));
    const lines = draft[task.id] ?? [];
    const hoursOf = (dk: string) => lines.find((l) => l.date === dk)?.plannedHours ?? 0;
    const firstHalf = seg.dates.slice(0, mid).map((dk) => ({ date: dk, plannedHours: hoursOf(dk) }));
    // Segunda metade adiada em 7 dias (cria o intervalo dos "dois momentos").
    const secondHalf = seg.dates.slice(mid).map((dk) => ({
      date: dateKey(addDays(parseKey(dk), 7)),
      plannedHours: hoursOf(dk),
    }));
    // Se o segmento tem 1 dia só, divide as horas em dois momentos.
    const built =
      seg.dates.length === 1
        ? [
            { date: seg.dates[0], plannedHours: Math.round((seg.total / 2) * 100) / 100 },
            { date: dateKey(addDays(seg.start, 7)), plannedHours: Math.round((seg.total / 2) * 100) / 100 },
          ]
        : [...firstHalf, ...secondHalf];
    replaceSegment(task, seg.dates, built);
  };

  // ── Linhas visíveis ───────────────────────────────────────────────────────
  //
  // Duas montagens diferentes, e não uma parametrizada: agrupar por épico é percorrer uma ÁRVORE
  // (a hierarquia é o dado), enquanto agrupar por projeto ou cliente é particionar uma LISTA por
  // uma chave. Espremer as duas na mesma função produziria uma árvore de um nível só fingindo ser
  // hierarquia — e o colapso, a indentação e o envelope do pai teriam de tratar o caso especial.
  const rows: GanttRow[] = [];

  if (groupBy === "epic") {
    // Tarefas com cronograma + seus ancestrais (épico / tarefa-pai).
    const visibleSet = new Set<number>();
    datedTasks.forEach((t) => {
      visibleSet.add(t.id);
      let p = t.parentTaskId ?? null;
      let g = 0;
      while (p != null && g++ < 12) {
        visibleSet.add(p);
        p = tree.byId.get(p)?.parentTaskId ?? null;
      }
    });
    const walk = (node: ProjectTask, depth: number) => {
      if (!visibleSet.has(node.id)) return;
      const kids = (tree.childrenOf.get(node.id) ?? []).filter((k) => visibleSet.has(k.id));
      rows.push({ kind: "task", task: node, depth, kids: kids.length > 0 });
      if (kids.length && !collapsed.has(node.id)) kids.forEach((k) => walk(k, depth + 1));
    };
    tree.topLevel.forEach((t) => walk(t, 0));
  } else {
    // Achatado: só quem TEM barra entra, agrupado pela chave. Repetir a hierarquia aqui mostraria
    // um épico dentro de um projeto dentro de um cliente — três níveis de envelope para a mesma
    // informação, que é justamente o que estas visões existem para não fazer.
    const chaveDe = (t: ProjectTask) => {
      if (groupBy === "project") return { key: `p${t.projectId}`, label: t.projectName ?? `Projeto #${t.projectId}` };

      const cliente = projectClients?.[t.projectId];
      return cliente?.clientId != null
        ? { key: `c${cliente.clientId}`, label: cliente.clientName ?? `Cliente #${cliente.clientId}` }
        : { key: "c0", label: "Sem cliente" };
    };

    const grupos = new Map<string, { label: string; tasks: ProjectTask[] }>();
    datedTasks.forEach((t) => {
      const { key, label } = chaveDe(t);
      const g = grupos.get(key) ?? { label, tasks: [] };
      g.tasks.push(t);
      grupos.set(key, g);
    });

    Array.from(grupos.entries())
      .sort((a, b) => a[1].label.localeCompare(b[1].label, "pt-BR"))
      .forEach(([key, g]) => {
        // Dentro do grupo, ordem cronológica: a lista existe para responder "o que vem primeiro",
        // e a ordem do board (orderIndex) não diz nada quando as tarefas vêm de projetos diferentes.
        const ordenadas = g.tasks.slice().sort((a, b) => {
          const sa = spanOfTask(a)?.getTime() ?? Infinity;
          const sb = spanOfTask(b)?.getTime() ?? Infinity;
          return sa - sb || a.title.localeCompare(b.title, "pt-BR");
        });

        rows.push({ kind: "group", key, label: g.label, tasks: ordenadas });
        if (!collapsedGroups.has(key)) ordenadas.forEach((t) => rows.push({ kind: "task", task: t, depth: 1, kids: false }));
      });
  }

  const epicIdOf = (t: ProjectTask): number | null => {
    let cur: ProjectTask | undefined = t;
    let g = 0;
    while (cur && g++ < 12) {
      if (cur.type === TaskType.Epic) return cur.id;
      if (cur.parentTaskId == null) return null;
      cur = tree.byId.get(cur.parentTaskId);
    }
    return null;
  };

  // Alguma folha descendente atrasada? (marca o envelope do pai/épico em vermelho)
  const subtreeOverdue = (id: number): boolean => {
    const kids = tree.childrenOf.get(id) ?? [];
    if (kids.length === 0) return tree.byId.get(id)?.scheduleState === "overdue";
    return kids.some((k) => subtreeOverdue(k.id));
  };

  // Envelope (menor início → maior prazo) das folhas descendentes — barra do pai/épico.
  const spanOf = (taskId: number): { start: Date; end: Date } | null => {
    let min = Infinity;
    let max = -Infinity;
    const collect = (id: number) => {
      const kids = tree.childrenOf.get(id) ?? [];
      if (kids.length === 0) {
        const lines = draft[id] ?? [];
        if (lines.length) {
          lines.forEach((l) => {
            const ms = parseKey(l.date).getTime();
            min = Math.min(min, ms);
            max = Math.max(max, ms);
          });
        } else {
          const node = tree.byId.get(id);
          if (node && (node.startDate || node.dueDate)) {
            min = Math.min(min, startOfDay(new Date(node.startDate || node.dueDate!)).getTime());
            max = Math.max(max, startOfDay(new Date(node.dueDate || node.startDate!)).getTime());
          }
        }
      } else kids.forEach((k) => collect(k.id));
    };
    collect(taskId);
    if (!isFinite(min)) return null;
    return { start: new Date(min), end: new Date(max) };
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
        <p className="text-[11px] text-gray-700 dark:text-gray-400">
          {editable
            ? "Arraste a barra para mover, a borda direita para redimensionar e ✂ para quebrar em 2 momentos. Faixas vermelhas = dia acima do capacity do responsável. Pinça (2 dedos) ou Ctrl+scroll = zoom; Shift+scroll = rolar na horizontal."
            : "Pinça com 2 dedos (ou Ctrl+scroll) = zoom entre dia / semana / mês. Shift+scroll rola na horizontal."}
        </p>
        <div className="flex shrink-0 items-center gap-2">
          <span className="rounded-lg bg-gray-100 px-2.5 py-1 text-[11px] font-semibold text-gray-700 dark:bg-navy-900 dark:text-gray-300">
            {GRAN_LABEL[granularity]}
          </span>
          <button type="button" onClick={doFit} title="Preencher a tela" className="h-8 rounded-lg border border-gray-200 px-2.5 text-[11px] font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-gray-600 dark:text-gray-200 dark:hover:bg-navy-700">Preencher</button>
          <button type="button" onClick={() => zoomBy(1 / 1.3)} aria-label="Afastar" title="Afastar" className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-base text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-gray-600 dark:text-gray-200 dark:hover:bg-navy-700">−</button>
          <button type="button" onClick={() => zoomBy(1.3)} aria-label="Aproximar" title="Aproximar" className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-base text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-gray-600 dark:text-gray-200 dark:hover:bg-navy-700">+</button>
        </div>
      </div>

      <div ref={scrollRef} className="overflow-x-auto">
        <div style={{ width: NAME_W + chartWidth }}>
          {/* Header */}
          <div className="flex border-b border-gray-100 pb-2 dark:border-white/10">
            <div className="sticky left-0 z-20 w-56 shrink-0 bg-white text-[11px] font-bold uppercase tracking-wide text-gray-700 dark:bg-navy-800 dark:text-gray-300">
              Tarefa
            </div>
            <div className="relative h-4" style={{ width: chartWidth }}>
              {columns.map((c, i) => (
                <div
                  key={i}
                  className={`absolute top-0 h-4 overflow-hidden whitespace-nowrap border-l pl-1 text-[10px] dark:border-white/10 ${
                    c.weekend ? "border-gray-50 text-gray-300 dark:text-gray-600" : "border-gray-100 text-gray-400"
                  }`}
                  style={{ left: c.leftPx, width: c.widthPx }}
                >
                  {c.label}
                </div>
              ))}
            </div>
          </div>

          {/* Linhas */}
          <div className="relative">
            {showToday && (
              <div
                className="pointer-events-none absolute inset-y-0 z-10 w-px bg-red-400"
                style={{ left: NAME_W + todayOffset * pxPerDay }}
                title="Hoje"
              />
            )}

            {rows.map((row) => {
              if (row.kind === "group") {
                // Envelope do grupo: do primeiro ao último dia de qualquer tarefa dele. Mesma
                // leitura visual do envelope de épico, para a linha significar a mesma coisa nas
                // três visões.
                const inicios = row.tasks.map(spanOfTask).filter((d): d is Date => d != null);
                const fins = row.tasks
                  .map((t) => {
                    const lines = draft[t.id];
                    if (lines?.length) return parseKey(lines.map((l) => l.date).sort().slice(-1)[0]);
                    const d = t.dueDate || t.startDate;
                    return d ? startOfDay(new Date(d)) : null;
                  })
                  .filter((d): d is Date => d != null);

                const inicio = inicios.length ? new Date(Math.min(...inicios.map((d) => d.getTime()))) : null;
                const fim = fins.length ? new Date(Math.max(...fins.map((d) => d.getTime()))) : null;
                const recolhido = collapsedGroups.has(row.key);
                const atrasadas = row.tasks.filter((t) => t.scheduleState === "overdue").length;

                return (
                  <div
                    key={row.key}
                    className="flex items-center border-b border-gray-100 bg-gray-50/60 py-1.5 dark:border-white/10 dark:bg-white/[0.03]"
                  >
                    <div className="sticky left-0 z-20 w-56 shrink-0 bg-gray-50 pr-2 dark:bg-navy-900">
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => toggleGroup(row.key)}
                          aria-label={recolhido ? "Expandir" : "Recolher"}
                          className="rounded p-0.5 text-gray-400 hover:bg-gray-200 hover:text-navy-700 dark:hover:bg-navy-700 dark:hover:text-white"
                        >
                          {recolhido ? <MdChevronRight size={16} /> : <MdExpandMore size={16} />}
                        </button>
                        <span
                          className="truncate text-xs font-bold text-navy-700 dark:text-white"
                          title={row.label}
                        >
                          {row.label}
                        </span>
                        <span className="ml-auto shrink-0 text-[10px] font-semibold text-gray-500 dark:text-gray-400">
                          {row.tasks.length}
                          {atrasadas > 0 && (
                            <span className="ml-1 text-red-600 dark:text-red-400">· {atrasadas} atrasada{atrasadas > 1 ? "s" : ""}</span>
                          )}
                        </span>
                      </div>
                    </div>
                    <div className="relative h-7" style={{ width: chartWidth }}>
                      {inicio && fim && (
                        <div
                          className={`absolute top-1/2 h-1.5 -translate-y-1/2 rounded-full ${
                            atrasadas > 0 ? "bg-red-300 dark:bg-red-500/50" : "bg-gray-300 dark:bg-white/20"
                          }`}
                          style={{
                            left: offsetOf(inicio) * pxPerDay,
                            width: Math.max(pxPerDay, (offsetOf(fim) - offsetOf(inicio) + 1) * pxPerDay),
                          }}
                          title={`${row.label}: ${fmtDM(inicio)} — ${fmtDM(fim)}`}
                        />
                      )}
                    </div>
                  </div>
                );
              }

              const { task, depth, kids } = row;
              const isParent = kids;
              const accent = epicColor(epicIdOf(task));
              const span = isParent ? spanOf(task.id) : null;
              const isOverdueRow = isParent ? subtreeOverdue(task.id) : task.scheduleState === "overdue";
              const lines = linesOf(task);
              const dailyCap = dailyCapOf(task.assigneeEmployeeId);
              // Dias do range em que o responsável estoura o capacity (overlay "não cabe").
              const overBands: number[] = [];
              if (!modoCliente && !isParent && task.assigneeEmployeeId != null) {
                for (let i = 0; i < totalDays; i++) {
                  const dk = dateKey(addDays(rangeStart, i));
                  const planned = plannedByEmpDay.get(`${task.assigneeEmployeeId}|${dk}`) ?? 0;
                  if (planned > dailyCap + 0.001) overBands.push(i);
                }
              }

              // Segmentos: do cronograma (editável) ou um bloco único derivado das datas (leitura).
              const segments: (Segment & { editable: boolean })[] = lines.length
                ? buildSegments(lines).map((s) => ({ ...s, editable }))
                : task.startDate || task.dueDate
                ? [
                    {
                      start: startOfDay(new Date(task.startDate || task.dueDate!)),
                      end: startOfDay(new Date(task.dueDate || task.startDate!)),
                      dates: [],
                      total: modoCliente ? 0 : task.estimatedHours ?? 0,
                      editable: false,
                    },
                  ]
                : [];

              return (
                <div key={task.id} className="flex items-center border-b border-gray-50 py-1.5 dark:border-white/5">
                  <div className="sticky left-0 z-20 w-56 shrink-0 bg-white pr-2 dark:bg-navy-800">
                    <div className="flex items-center gap-1" style={{ paddingLeft: depth * 14 }}>
                      <button
                        type="button"
                        onClick={() => kids && toggle(task.id)}
                        aria-label={collapsed.has(task.id) ? "Expandir" : "Recolher"}
                        className={`flex h-4 w-4 shrink-0 items-center justify-center text-gray-400 ${kids ? "hover:text-gray-700 dark:hover:text-gray-200" : "invisible"}`}
                        title={collapsed.has(task.id) ? "Expandir" : "Recolher"}
                      >
                        {collapsed.has(task.id) ? <MdChevronRight className="h-4 w-4" aria-hidden="true" /> : <MdExpandMore className="h-4 w-4" aria-hidden="true" />}
                      </button>
                      <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: accent.solid }} />
                      <span
                        className={`truncate text-xs ${task.type === TaskType.Epic ? "font-bold" : "font-medium"} text-navy-700 dark:text-white`}
                        title={task.title}
                      >
                        {task.title}
                      </span>
                      {isOverdueRow && (
                        <span
                          className="shrink-0 rounded-full bg-red-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-red-700 dark:bg-red-900/40 dark:text-red-300"
                          title={isParent ? "Contém tarefa(s) atrasada(s)" : "Tarefa atrasada"}
                        >
                          Atrasada
                        </span>
                      )}
                    </div>
                    {!isParent && (modoCliente ? showProject && task.projectName : true) && (
                      <p className="truncate pl-6 text-[10px] text-gray-700 dark:text-gray-400">
                        {modoCliente
                          ? task.projectName
                          : showProject && task.projectName
                          ? `${task.projectName} · ${task.assigneeName ?? "Sem responsável"}`
                          : task.assigneeName ?? "Sem responsável"}
                      </p>
                    )}
                  </div>
                  <div className="relative h-7" style={{ width: chartWidth }}>
                    {/* Barra-envelope do pai/épico (não editável; envolve as filhas) */}
                    {isParent && span && (
                      <div
                        className={`absolute top-1/2 h-2.5 -translate-y-1/2 cursor-pointer rounded-full ${
                          isOverdueRow ? "ring-2 ring-red-500" : ""
                        }`}
                        style={{
                          left: offsetOf(span.start) * pxPerDay,
                          width: Math.max(6, ((span.end.getTime() - span.start.getTime()) / DAY_MS + 1) * pxPerDay),
                          background: accent.solid,
                          opacity: 0.5,
                        }}
                        title={`${task.title} — ${fmtDM(span.start)} a ${fmtDM(span.end)}${
                          isOverdueRow ? " · contém tarefa(s) atrasada(s)" : ""
                        }`}
                        onClick={() => onEdit(task)}
                      />
                    )}
                    {/* Folhas: faixas vermelhas nos dias acima do capacity */}
                    {!isParent &&
                      overBands.map((i) => (
                      <div
                        key={`ov-${i}`}
                        className="pointer-events-none absolute inset-y-0 bg-red-400/15"
                        style={{ left: i * pxPerDay, width: pxPerDay }}
                        title="Acima do capacity do responsável neste dia"
                      />
                    ))}

                    {!isParent &&
                      segments.map((seg, si) => {
                      const isDragging = drag && drag.taskId === task.id && drag.segIndex === si;
                      const delta = isDragging ? drag!.deltaDays : 0;
                      const spanDays = Math.round((seg.end.getTime() - seg.start.getTime()) / DAY_MS) + 1;
                      const baseOffset = offsetOf(seg.start);
                      const offset = baseOffset + (isDragging && drag!.mode === "move" ? delta : 0);
                      const duration =
                        isDragging && drag!.mode === "resize" ? clamp(spanDays + delta, 1, 60) : spanDays;
                      // Algum dia do segmento estoura o capacity?
                      const segOver =
                        !modoCliente &&
                        task.assigneeEmployeeId != null &&
                        seg.dates.some(
                          (dk) => (plannedByEmpDay.get(`${task.assigneeEmployeeId}|${dk}`) ?? 0) > dailyCap + 0.001
                        );

                      return (
                        <div
                          key={si}
                          className={`group absolute top-1/2 flex h-5 -translate-y-1/2 items-center rounded-md px-2 text-[10px] font-semibold text-white ${barColor(
                            task
                          )} ${seg.editable ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"} ${
                            segOver ? "ring-2 ring-red-500" : ""
                          } ${isDragging ? "opacity-80 shadow-lg" : ""}`}
                          style={{
                            left: offset * pxPerDay,
                            width: duration * pxPerDay,
                            minWidth: "10px",
                            ...(task.scheduleState === "done" ? { backgroundImage: DONE_STRIPES } : {}),
                          }}
                          title={`${task.title} — ${fmtDM(seg.start)} a ${fmtDM(seg.end)}${
                            seg.total ? ` (${seg.total}h)` : ""
                          }${segOver ? " · acima do capacity" : ""}`}
                          onMouseDown={(e) => {
                            if (!seg.editable) return;
                            e.preventDefault();
                            didDragRef.current = false;
                            setDrag({ taskId: task.id, segIndex: si, mode: "move", startX: e.clientX, deltaDays: 0 });
                          }}
                          onClick={() => {
                            // Abre a edição só no clique simples (não logo após arrastar).
                            if (didDragRef.current) {
                              didDragRef.current = false;
                              return;
                            }
                            onEdit(task);
                          }}
                        >
                          <span className="truncate">{seg.total ? `${seg.total}h` : ""}</span>

                          {seg.editable && (
                            <>
                              {/* botão quebrar */}
                              <button
                                type="button"
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  splitSegment(task, seg);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                title="Quebrar em 2 momentos"
                                className="ml-1 hidden rounded bg-white/25 px-0.5 hover:bg-white/40 group-hover:inline-flex"
                              >
                                <MdCallSplit size={11} />
                              </button>
                              {/* handle de redimensionar (borda direita) */}
                              <span
                                onMouseDown={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  didDragRef.current = false;
                                  setDrag({ taskId: task.id, segIndex: si, mode: "resize", startX: e.clientX, deltaDays: 0 });
                                }}
                                onClick={(e) => e.stopPropagation()}
                                title="Redimensionar"
                                className="absolute right-0 top-0 h-full w-1.5 cursor-ew-resize rounded-r-md bg-white/40 opacity-0 group-hover:opacity-100"
                              />
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-1 flex flex-wrap gap-3 text-[11px] text-gray-700 dark:text-gray-400">
        <span className="flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-brand-500" aria-hidden="true" /> No prazo</span>
        <span className="flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-amber-500" aria-hidden="true" /> Vence em breve</span>
        <span className="flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-red-500" aria-hidden="true" /> Atrasada</span>
        <span className="flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-green-500" style={{ backgroundImage: DONE_STRIPES }} aria-hidden="true" /> Concluída</span>
        {!modoCliente && (
          <span className="flex items-center gap-1"><i className="h-2.5 w-2.5 rounded-sm bg-red-400/40" aria-hidden="true" /> Acima do capacity</span>
        )}
      </div>

      {undated.length > 0 && (
        <p className="text-[11px] text-gray-700 dark:text-gray-400">
          {undated.length} tarefa(s) sem cronograma (no Backlog ou sem responsável/estimativa) não aparecem aqui.
        </p>
      )}
    </div>
  );
};

export default TaskGantt;
